import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import ImageUploader from '../../components/ImageUploader';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Newspaper, RefreshCw, Eye } from 'lucide-react';

const AdminNews = () => {
  const { token } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', image_url: '', author: 'Killarney Connect' });

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNews(); }, []);

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Please fill in title and content');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/news/${editing}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Article updated!');
      } else {
        await axios.post(`${API}/news`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Article published!');
      }
      setShowDialog(false);
      setFormData({ title: '', content: '', image_url: '', author: 'Killarney Connect' });
      setEditing(null);
      fetchNews();
    } catch (error) {
      toast.error('Failed to save article');
    }
  };

  const handleEdit = (item) => {
    setFormData({ title: item.title, content: item.content, image_url: item.image_url || '', author: item.author });
    setEditing(item.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/news/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Article deleted!');
      fetchNews();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Newspaper className="w-6 h-6 text-plp-blue" /> Community News
          </h1>
          <p className="text-slate-600">Create and manage news articles for the community</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) { setEditing(null); setFormData({ title: '', content: '', image_url: '', author: 'Killarney Connect' }); }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-plp-blue hover:bg-plp-blue-light" data-testid="add-news-btn">
              <Plus className="w-4 h-4 mr-2" /> New Article
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Article' : 'New Article'}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Title *</Label>
                <Input 
                  value={formData.title} 
                  onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} 
                  placeholder="Enter article headline"
                  className="text-lg"
                />
              </div>
              <div>
                <Label>Author</Label>
                <Input 
                  value={formData.author} 
                  onChange={(e) => setFormData(p => ({ ...p, author: e.target.value }))} 
                  placeholder="Article author"
                />
              </div>
              
              <ImageUploader 
                label="Featured Image"
                value={formData.image_url}
                onChange={(url) => setFormData(p => ({ ...p, image_url: url }))}
              />

              <div>
                <Label>Content *</Label>
                <Textarea 
                  value={formData.content} 
                  onChange={(e) => setFormData(p => ({ ...p, content: e.target.value }))} 
                  placeholder="Write your article content here..."
                  rows={10}
                  className="font-body"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSubmit} className="flex-1 bg-plp-blue">
                  {editing ? 'Update Article' : 'Publish Article'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-plp-blue" /></div>
      ) : news.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No news articles yet</p>
            <Button onClick={() => setShowDialog(true)} variant="link" className="text-plp-blue mt-2">
              Create your first article
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {news.map((item) => (
            <Card key={item.id} className="bg-white overflow-hidden" data-testid={`news-${item.id}`}>
              <CardContent className="p-0">
                <div className="flex">
                  {item.image_url && (
                    <div className="w-48 flex-shrink-0">
                      <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-lg">{item.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">By {item.author} • {new Date(item.created_at).toLocaleDateString()}</p>
                        <p className="text-slate-600 mt-2 line-clamp-2">{item.content}</p>
                      </div>
                      <div className="flex gap-1 ml-4">
                        <Button variant="ghost" size="icon" asChild>
                          <a href={`/news`} target="_blank" rel="noopener noreferrer">
                            <Eye className="w-4 h-4" />
                          </a>
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminNews;
